import React, { useState, useEffect, useCallback } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import Landing from './pages/Landing';
import Labs from './pages/Labs';
import PitchDeck from './pages/PitchDeck';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import ModeSelection from './pages/ModeSelection';
import RoleSelection from './pages/RoleSelection';
import UnifiedDashboard from './pages/UnifiedDashboard';
import DiseaseProfile from './pages/DiseaseProfile';
import ReportInterpreter from './pages/ReportInterpreter';
import DiagnoseLanding from './pages/DiagnoseLanding';
import LetsDiagnose from './pages/LetsDiagnose';
import ConsultDoctor from './pages/ConsultDoctor';
import DoctorFinder from './pages/DoctorFinder';
import Research from './pages/Research';
import History from './pages/History';
import Profile from './pages/Profile';
import ProtectedRoute from './components/ProtectedRoute';
import { ChatWidget } from './components/chatbot';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminStatsPage from './pages/admin/AdminStatsPage';
import AdminBookingsPage from './pages/admin/AdminBookingsPage';

// MedPlexity Labs Pages
import MedPlexityLabs from './pages/labs/MedPlexityLabs';
import LabsAnalysisPage from './pages/labs/AnalysisPage';
import LabsHistoryPage from './pages/labs/HistoryPage';
import LabReportsPage from './pages/labs/LabReportsPage';
import LabReportAnalysisPage from './pages/labs/LabReportAnalysisPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin Auth Hook
const useAdminAuth = () => {
  const [token, setToken] = useState(localStorage.getItem("admin_token"));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const verifyToken = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem("admin_token");
        setToken(null);
        setIsAuthenticated(false);
      }
    } catch {
      localStorage.removeItem("admin_token");
      setToken(null);
      setIsAuthenticated(false);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const login = (newToken) => {
    setToken(newToken);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
    setIsAuthenticated(false);
  };

  return { token, isAuthenticated, loading, login, logout };
};

// Admin Protected Route
const AdminProtectedRoute = ({ children, isAuthenticated, loading }) => {
  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-violet-500"></div>
      </div>
    );
  }
  return isAuthenticated ? children : <Navigate to="/admin" replace />;
};

function AppRouter() {
  const location = useLocation();
  const { token, isAuthenticated, loading, login, logout } = useAdminAuth();

  // Check for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Don't show chat widget on admin pages
  const isAdminPage = location.pathname.startsWith('/admin');

  return (
    <>
      <Routes>
        <Route path="/" element={<Labs />} />
        <Route path="/join-beta" element={<Login />} />
        <Route path="/login" element={<Login />} />
        <Route path="/role-selection" element={<RoleSelection />} />
        <Route path="/mode-selection" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <UnifiedDashboard />
          </ProtectedRoute>
        } />
        <Route path="/disease/:name" element={
          <ProtectedRoute>
            <DiseaseProfile />
          </ProtectedRoute>
        } />
        <Route path="/report-interpreter" element={
          <ProtectedRoute>
            <Navigate to="/labs" replace />
          </ProtectedRoute>
        } />
        <Route path="/diagnosis" element={
          <ProtectedRoute>
            <DiagnoseLanding />
          </ProtectedRoute>
        } />
        <Route path="/diagnose-chat" element={
          <ProtectedRoute>
            <LetsDiagnose />
          </ProtectedRoute>
        } />
        <Route path="/doctor-finder" element={
          <ProtectedRoute>
            <DoctorFinder />
          </ProtectedRoute>
        } />
        <Route path="/consult-doctor" element={
          <ProtectedRoute>
            <ConsultDoctor />
          </ProtectedRoute>
        } />
        <Route path="/research" element={
          <ProtectedRoute>
            <Research />
          </ProtectedRoute>
        } />
        <Route path="/history" element={
          <ProtectedRoute>
            <History />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } />
        
        {/* MedPlexity Labs Routes */}
        <Route path="/labs" element={
          <ProtectedRoute>
            <MedPlexityLabs />
          </ProtectedRoute>
        } />
        <Route path="/labs/analysis/:id" element={
          <ProtectedRoute>
            <LabsAnalysisPage />
          </ProtectedRoute>
        } />
        <Route path="/labs/history" element={
          <ProtectedRoute>
            <LabsHistoryPage />
          </ProtectedRoute>
        } />
        <Route path="/labs/lab-reports" element={
          <ProtectedRoute>
            <LabReportsPage />
          </ProtectedRoute>
        } />
        <Route path="/labs/lab-report/:id" element={
          <ProtectedRoute>
            <LabReportAnalysisPage />
          </ProtectedRoute>
        } />
        
        {/* Admin Routes */}
        <Route 
          path="/admin" 
          element={
            isAuthenticated ? 
              <Navigate to="/admin/dashboard" replace /> : 
              <AdminLoginPage onLogin={login} />
          } 
        />
        <Route 
          path="/admin/dashboard" 
          element={
            <AdminProtectedRoute isAuthenticated={isAuthenticated} loading={loading}>
              <AdminDashboardPage token={token} onLogout={logout} />
            </AdminProtectedRoute>
          } 
        />
        <Route 
          path="/admin/stats" 
          element={
            <AdminProtectedRoute isAuthenticated={isAuthenticated} loading={loading}>
              <AdminStatsPage token={token} />
            </AdminProtectedRoute>
          } 
        />
        <Route 
          path="/admin/bookings" 
          element={
            <AdminProtectedRoute isAuthenticated={isAuthenticated} loading={loading}>
              <AdminBookingsPage token={token} />
            </AdminProtectedRoute>
          } 
        />
      </Routes>
      
      {/* Dr. Veena AI Chatbot - Available on all pages except admin */}
      {!isAdminPage && <ChatWidget />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
